import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import WhatsAppHeader from "./WhatsAppHeader";
import { toggleSidebar } from "../../utils/sidebarToggle";
import {
  getWhatsAppReports,
  sendWhatsReport,
  searchCustomers,
  getReportPdf,
} from "../../services/whatsAppReportService";
import { errorAlert, successAlert } from "../common/Swal";

// ─── constants ────────────────────────────────────────────────────────────────
const MAX_ATTEMPTS = 5;
const PAGE_SIZE = 10;

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-PK", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

// ─── STATUS CONFIG ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  sent: { label: "Sent", dot: "#22c55e", bg: "#f0fdf4", color: "#15803d" },
  failed: { label: "Failed", dot: "#ef4444", bg: "#fef2f2", color: "#dc2626" },
  pending: {
    label: "Pending",
    dot: "#f59e0b",
    bg: "#fffbeb",
    color: "#b45309",
  },
  exhausted: {
    label: "Exhausted",
    dot: "#94a3b8",
    bg: "#f8fafc",
    color: "#64748b",
  },
};

// ─── MICRO COMPONENTS ─────────────────────────────────────────────────────────
function StatusPill({ status }) {
  const c = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 11px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.04em",
        background: c.bg,
        color: c.color,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: c.dot,
          flexShrink: 0,
        }}
      />
      {c.label.toUpperCase()}
    </span>
  );
}

function SectionPill({ section }) {
  if (!section)
    return <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>;
  const s =
    section === "receive"
      ? { bg: "#ecfdf5", color: "#065f46" }
      : { bg: "#fff7ed", color: "#9a3412" };
  return (
    <span
      style={{
        padding: "3px 9px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        background: s.bg,
        color: s.color,
        textTransform: "capitalize",
        letterSpacing: "0.03em",
      }}
    >
      {section}
    </span>
  );
}

function AttemptBar({ attempts, status }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
      {Array.from({ length: MAX_ATTEMPTS }, (_, i) => {
        const filled = i < attempts;
        const color = !filled
          ? "#e2e8f0"
          : status === "sent"
            ? "#22c55e"
            : status === "exhausted"
              ? "#94a3b8"
              : "#f87171";
        return (
          <span
            key={i}
            title={`Attempt ${i + 1}`}
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: color,
              flexShrink: 0,
              transition: "background 0.2s",
            }}
          />
        );
      })}
      <span
        style={{
          fontSize: 10,
          color: "#94a3b8",
          marginLeft: 3,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {attempts}/{MAX_ATTEMPTS}
      </span>
    </div>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        border: "1px solid #f1f5f9",
        padding: "16px 20px",
        minWidth: 0,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 800,
          color: "#cbd5e1",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 800,
          color: accent || "#0f172a",
          lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ─── EXPAND DETAIL ────────────────────────────────────────────────────────────
function ExpandDetail({ row, onResend, onView, isSending, isViewing }) {
  return (
    <div
      style={{
        background: "#fafafa",
        borderTop: "1px solid #f1f5f9",
        borderBottom: "2px solid #e8edf2",
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr",
      }}
    >
      {/* Timing */}
      <div style={{ padding: "16px 20px", borderRight: "1px solid #f1f5f9" }}>
        <p style={detailLabel}>Timing</p>
        <DetailRow label="Created" value={fmt(row.createdAt)} />
        <DetailRow label="Last attempt" value={fmt(row.lastAttemptAt)} />
        {row.sentAt && (
          <DetailRow
            label="Sent at"
            value={fmt(row.sentAt)}
            valueColor="#15803d"
          />
        )}
      </div>

      {/* Attempts */}
      <div style={{ padding: "16px 20px", borderRight: "1px solid #f1f5f9" }}>
        <p style={detailLabel}>Attempts</p>
        <div style={{ marginBottom: 10 }}>
          <AttemptBar attempts={row.attempts} status={row.status} />
        </div>
        <DetailRow label="TX ID" value={row.transactionId} mono />
      </div>

      {/* Error */}
      <div style={{ padding: "16px 20px", borderRight: "1px solid #f1f5f9" }}>
        <p style={detailLabel}>Error</p>
        {row.error ? (
          <div
            style={{
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: 11,
              color: "#dc2626",
              lineHeight: 1.6,
              wordBreak: "break-word",
            }}
          >
            {row.error}
          </div>
        ) : (
          <span style={{ fontSize: 12, color: "#94a3b8" }}>No errors</span>
        )}
      </div>

      {/* Actions */}
      <div
        style={{
          padding: "16px 20px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
          justifyContent: "center",
        }}
      >
        <p style={detailLabel}>Actions</p>

        {/* View PDF */}
        <ActionBtn
          label={isViewing ? "Loading…" : "View PDF"}
          icon="📄"
          onClick={onView}
          disabled={isViewing}
          color="#1d4ed8"
          bg="#eff6ff"
          border="#bfdbfe"
        />

        {/* Resend */}
        {row.status !== "sent" && (
          <ActionBtn
            label={isSending ? "Sending…" : "Resend"}
            icon="↺"
            onClick={onResend}
            disabled={isSending}
            color="#15803d"
            bg="#f0fdf4"
            border="#bbf7d0"
          />
        )}
      </div>
    </div>
  );
}

const detailLabel = {
  fontSize: 10,
  fontWeight: 800,
  color: "#cbd5e1",
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  marginBottom: 10,
  marginTop: 0,
};

function DetailRow({ label, value, mono, valueColor }) {
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 10, color: "#94a3b8", marginBottom: 1 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: valueColor || "#334155",
          fontFamily: mono ? "'DM Mono', monospace" : "inherit",
          wordBreak: "break-all",
        }}
      >
        {value || "—"}
      </div>
    </div>
  );
}

function ActionBtn({ label, icon, onClick, disabled, color, bg, border }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "7px 14px",
        borderRadius: 8,
        border: `1px solid ${border}`,
        background: disabled ? "#f8fafc" : bg,
        color: disabled ? "#94a3b8" : color,
        fontSize: 12,
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.7 : 1,
        transition: "all 0.15s",
        width: "100%",
        justifyContent: "center",
      }}
    >
      <span style={{ fontSize: 14 }}>{icon}</span> {label}
    </button>
  );
}

// ─── SEARCH DROPDOWN ──────────────────────────────────────────────────────────
function SearchDropdown({ value, onChange, onSelect, results, loading, show }) {
  return (
    <div style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <span
          style={{
            position: "absolute",
            left: 10,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#94a3b8",
            fontSize: 14,
            pointerEvents: "none",
          }}
        >
          🔍
        </span>
        <input
          type="text"
          placeholder="Search by customer name, phone or serial number"
          value={value}
          onChange={onChange}
          style={{
            height: 38,
            paddingLeft: 32,
            paddingRight: 12,
            borderRadius: 9,
            border: "1px solid #e2e8f0",
            fontSize: 13,
            color: "#334155",
            background: "#fff",
            outline: "none",
            width: 280,
            fontFamily: "'DM Sans', sans-serif",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        />
      </div>
      {/* {show && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            width: 320,
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 10,
            boxShadow: "0 8px 30px rgba(0,0,0,0.10)",
            zIndex: 100,
            overflow: "hidden",
            maxHeight: 280,
            overflowY: "auto",
          }}
        >
          {loading && (
            <div
              style={{
                padding: "14px 16px",
                fontSize: 12,
                color: "#94a3b8",
                textAlign: "center",
              }}
            >
              Searching…
            </div>
          )}
          {!loading && results.length === 0 && (
            <div
              style={{
                padding: "14px 16px",
                fontSize: 12,
                color: "#94a3b8",
                textAlign: "center",
              }}
            >
              No results found
            </div>
          )}
          {!loading &&
            results.map((r) => (
              <button
                key={r._id}
                onClick={() => onSelect(r)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  borderBottom: "1px solid #f8fafc",
                  padding: "10px 16px",
                  cursor: "pointer",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f8fafc")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "none")
                }
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 13,
                    color: "#0f172a",
                    marginBottom: 2,
                  }}
                >
                  {r.name}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#94a3b8",
                    fontFamily: "'DM Mono', monospace",
                  }}
                >
                  {r.phone} · {r.serialNo || ""}
                </div>
              </button>
            ))}
        </div>
      )} */}
    </div>
  );
}

// ─── PAGINATION ───────────────────────────────────────────────────────────────
function Pagination({ page, totalPages, onChange }) {
  const pages = [];
  const delta = 2;
  const left = Math.max(2, page - delta);
  const right = Math.min(totalPages - 1, page + delta);

  pages.push(1);
  if (left > 2) pages.push("...");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < totalPages - 1) pages.push("...");
  if (totalPages > 1) pages.push(totalPages);

  const btnStyle = (active, disabled) => ({
    height: 32,
    minWidth: 32,
    padding: "0 8px",
    borderRadius: 7,
    border: active ? "1.5px solid #1d4ed8" : "1px solid #e2e8f0",
    background: active ? "#1d4ed8" : disabled ? "#f8fafc" : "#fff",
    color: active ? "#fff" : disabled ? "#cbd5e1" : "#334155",
    fontSize: 13,
    fontWeight: active ? 700 : 500,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.15s",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
  });

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 4,
        justifyContent: "center",
        marginTop: 20,
      }}
    >
      <button
        style={btnStyle(false, page === 1)}
        disabled={page === 1}
        onClick={() => onChange(page - 1)}
      >
        ‹
      </button>
      {pages.map((p, i) =>
        p === "..." ? (
          <span
            key={`e${i}`}
            style={{ fontSize: 13, color: "#94a3b8", padding: "0 4px" }}
          >
            …
          </span>
        ) : (
          <button
            key={p}
            style={btnStyle(p === page, false)}
            onClick={() => onChange(p)}
          >
            {p}
          </button>
        ),
      )}
      <button
        style={btnStyle(false, page === totalPages)}
        disabled={page === totalPages}
        onClick={() => onChange(page + 1)}
      >
        ›
      </button>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
export default function WhatsAppReportTable() {
  const [searchParams, setSearchParams] = useSearchParams();

  // ── state from URL params ───────────────────────────────────────────────────
  const [page, setPage] = useState(Number(searchParams.get("page")) || 1);
  const [filterStatus, setFS] = useState(searchParams.get("status") || "");
  const [filterSection, setFSec] = useState(searchParams.get("section") || "");
  const [searchQuery, setSearch] = useState(searchParams.get("search") || "");

  // ── local state ─────────────────────────────────────────────────────────────
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    sent: 0,
    pending: 0,
    failed: 0,
    exhausted: 0,
  });
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [expanded, setExpanded] = useState(new Set());
  const [sending, setSending] = useState(new Set());
  const [viewing, setViewing] = useState(new Set());
  const [loading, setLoading] = useState(false);

  // search dropdown
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [dropResults, setDropResults] = useState([]);
  const [dropLoading, setDropLoading] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const searchDebounce = useRef(null);
  const dropRef = useRef(null);

  // ── sync URL → state ────────────────────────────────────────────────────────
  const updateParams = useCallback(
    (updates) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        let shouldResetPage = false;

        Object.entries(updates).forEach(([k, v]) => {
          const prevVal = prev.get(k) || "";

          if (v) next.set(k, v);
          else next.delete(k);

          // ✅ check if filter value actually changed
          if (prevVal !== (v || "")) {
            if (k !== "page") shouldResetPage = true;
          }
        });

        // ✅ only reset page if filter changed AND not already page 1
        if (shouldResetPage && next.get("page") !== "1") {
          next.set("page", "1");
        }

        return next;
      });
    },
    [setSearchParams],
  );

  // ── fetch reports ───────────────────────────────────────────────────────────
  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: searchParams.get("page") || 1,
        limit: PAGE_SIZE,
        status: searchParams.get("status") || "",
        section: searchParams.get("section") || "",
        search: searchParams.get("search") || "",
      };
      const data = await getWhatsAppReports(params);
      setReports(data?.data || []);
      setStats(
        data?.stats || {
          total: 0,
          sent: 0,
          pending: 0,
          failed: 0,
          exhausted: 0,
        },
      );
      setPagination(data?.pagination || { total: 0, totalPages: 1 });
      setPage(Number(searchParams.get("page")) || 1);
    } catch (err) {
      console.error("Error fetching reports:", err);
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // ── close dropdown on outside click ─────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (dropRef.current && !dropRef.current.contains(e.target))
        setShowDrop(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (searchDebounce.current) {
      clearTimeout(searchDebounce.current);
    }

    searchDebounce.current = setTimeout(() => {
      updateParams({
        search: searchInput.trim() || "",
      });
    }, 500);
  }, [searchInput]);

  const handleSearchInput = (e) => {
    setSearchInput(e.target.value);
  };

  const handleDropSelect = (item) => {
    setSearchInput(item.name);
    setShowDrop(false);
    updateParams({ search: item._id }); // pass customer ID to backend for precise filtering
  };

  // ── filter handlers ──────────────────────────────────────────────────────────
  const handleStatusChange = (val) => {
    setFS(val);
    updateParams({ status: val });
  };
  const handleSectionChange = (val) => {
    setFSec(val);
    updateParams({ section: val });
  };
  const handlePageChange = (p) => {
    setPage(p);
    setSearchParams((prev) => {
      const n = new URLSearchParams(prev);
      n.set("page", p);
      return n;
    });
  };

  const clearFilters = () => {
    setFS("");
    setFSec("");
    setSearch("");
    setSearchInput("");
    setSearchParams({});
  };

  // ── row actions ──────────────────────────────────────────────────────────────
  const handleResend = useCallback(
    async (e, row) => {
      e.stopPropagation();
      setSending((prev) => new Set(prev).add(row._id));
      try {
        const res = await sendWhatsReport(row._id);
        if (res.success) {
          successAlert("Report queued for sending");
          await fetchReports();
        } else errorAlert(res.message || "Failed to send");
      } catch (err) {
        errorAlert(err.message);
      } finally {
        setSending((prev) => {
          const n = new Set(prev);
          n.delete(row._id);
          return n;
        });
      }
    },
    [fetchReports],
  );

  const handleView = useCallback(async (e, row) => {
    e.stopPropagation();
    setViewing((prev) => new Set(prev).add(row._id));
    try {
      const res = await getReportPdf(row._id);
      // ✅ Create blob URL
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);

      // ✅ Open in new tab
      window.open(url, "_blank");
    } catch (err) {
      errorAlert(err.message);
    } finally {
      setViewing((prev) => {
        const n = new Set(prev);
        n.delete(row._id);
        return n;
      });
    }
  }, []);

  const toggleExpand = useCallback((id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const hasFilters =
    filterStatus || filterSection || searchQuery || searchInput;

  // ── TH style ─────────────────────────────────────────────────────────────────
  const th = {
    padding: "11px 14px",
    textAlign: "left",
    fontSize: 10,
    fontWeight: 800,
    color: "#94a3b8",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    borderBottom: "2px solid #f1f5f9",
    whiteSpace: "nowrap",
    background: "#fafafa",
  };

  return (
    <>
      <WhatsAppHeader toggleSidebar={toggleSidebar} />

      <div className="inventory-container px-4 py-4">
        <link
          href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,600;0,9..40,700;0,9..40,800;1,9..40,400&family=DM+Mono:wght@400;500&display=swap"
          rel="stylesheet"
        />

        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
          .wa-row:hover td { background: #f8fafc !important; }
          .wa-action:hover { opacity: 0.85; transform: scale(0.97); }
          ::-webkit-scrollbar { width: 6px; height: 6px; }
          ::-webkit-scrollbar-track { background: #f8fafc; }
          ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 99px; }
          ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
        `}</style>

        <div style={{ fontFamily: "'DM Sans', sans-serif", color: "#0f172a" }}>
          {/* ── Page title ── */}
          {/* <div style={{ marginBottom: 24 }}>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, letterSpacing: "-0.02em" }}>WhatsApp Reports</h1>
            <p style={{ fontSize: 13, color: "#94a3b8", margin: "4px 0 0" }}>
              Track & manage delivery status of reports sent to customers
            </p>
          </div> */}

          {/* ── Stat cards ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 12,
              marginBottom: 24,
            }}
          >
            <StatCard label="Total" value={stats.total} accent="#0f172a" />
            <StatCard label="Sent" value={stats.sent} accent="#15803d" />
            <StatCard label="Pending" value={stats.pending} accent="#b45309" />
            <StatCard label="Failed" value={stats.failed} accent="#dc2626" />
            <StatCard
              label="Exhausted"
              value={stats.exhausted}
              accent="#64748b"
            />
          </div>

          {/* ── Filters bar ── */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              marginBottom: 16,
              flexWrap: "wrap",
            }}
          >
            {/* Search with dropdown */}
            <div ref={dropRef}>
              <SearchDropdown
                value={searchInput}
                onChange={handleSearchInput}
                onSelect={handleDropSelect}
                results={dropResults}
                loading={dropLoading}
                show={showDrop}
              />
            </div>

            {/* Status filter */}
            <FilterSelect
              value={filterStatus}
              onChange={handleStatusChange}
              placeholder="All statuses"
              options={[
                { value: "sent", label: "Sent" },
                { value: "pending", label: "Pending" },
                { value: "failed", label: "Failed" },
                { value: "exhausted", label: "Exhausted" },
              ]}
            />

            {/* Section filter */}
            <FilterSelect
              value={filterSection}
              onChange={handleSectionChange}
              placeholder="All sections"
              options={[
                { value: "receive", label: "Receive" },
                { value: "given", label: "Given" },
                { value: "both", label: "Both rc/gv" },
              ]}
            />

            {/* Clear */}
            {hasFilters && (
              <button
                onClick={clearFilters}
                style={{
                  height: 38,
                  padding: "0 14px",
                  borderRadius: 9,
                  border: "1px solid #fecdd3",
                  background: "#fff1f2",
                  color: "#dc2626",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                ✕ Clear
              </button>
            )}

            {/* Results count */}
            <span
              style={{ marginLeft: "auto", fontSize: 12, color: "#94a3b8" }}
            >
              {pagination.total} result{pagination.total !== 1 ? "s" : ""}
            </span>
          </div>

          {/* ── Table wrapper with scrollbar ── */}
          <div
            style={{
              border: "1px solid #f1f5f9",
              borderRadius: 14,
              overflow: "hidden",
              background: "#fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            }}
          >
            <div
              style={{
                overflowX: "auto",
                overflowY: "auto",
                maxHeight: "50vh",
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: 700,
                }}
              >
                <thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
                  <tr>
                    <th style={{ ...th, width: 36 }}></th>
                    <th style={{ ...th, width: 100 }}>Serial</th>
                    <th style={{ ...th, width: 200 }}>Customer</th>
                    <th style={{ ...th, width: 90 }}>Section</th>
                    <th style={{ ...th, width: 120 }}>Status</th>
                    <th style={{ ...th, width: 110, textAlign: "center" }}>
                      Attempts
                    </th>
                    <th style={{ ...th, width: 90 }}>Sent At</th>
                    <th style={{ ...th, width: 80, textAlign: "center" }}>
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{
                          textAlign: "center",
                          padding: "52px 0",
                          color: "#94a3b8",
                          fontSize: 14,
                        }}
                      >
                        <span
                          style={{
                            display: "inline-block",
                            animation: "spin 0.9s linear infinite",
                            fontSize: 20,
                            marginBottom: 8,
                          }}
                        >
                          ⟳
                        </span>
                        <br />
                        Loading reports…
                      </td>
                    </tr>
                  )}

                  {!loading && reports.length === 0 && (
                    <tr>
                      <td
                        colSpan={8}
                        style={{
                          textAlign: "center",
                          padding: "52px 0",
                          color: "#94a3b8",
                          fontSize: 14,
                        }}
                      >
                        No reports found.
                      </td>
                    </tr>
                  )}

                  {!loading &&
                    reports.map((row) => {
                      const isOpen = expanded.has(row._id);
                      const isSending = sending.has(row._id);
                      const isViewing = viewing.has(row._id);

                      return (
                        <>
                          <tr
                            key={row._id}
                            className="wa-row"
                            onClick={() => toggleExpand(row._id)}
                            style={{
                              cursor: "pointer",
                              background: isOpen ? "#f8fafc" : "#fff",
                              borderTop: "1px solid #f8fafc",
                              transition: "background 0.12s",
                            }}
                          >
                            {/* chevron */}
                            <td
                              style={{
                                padding: "0 0 0 14px",
                                textAlign: "center",
                                width: 36,
                              }}
                            >
                              <span
                                style={{
                                  display: "inline-block",
                                  fontSize: 13,
                                  color: "#cbd5e1",
                                  transition: "transform 0.18s",
                                  transform: isOpen ? "rotate(180deg)" : "none",
                                }}
                              >
                                ▾
                              </span>
                            </td>

                            {/* serial */}
                            <td
                              style={{
                                padding: "13px 14px",
                                fontSize: 12,
                                color: "#64748b",
                                fontFamily: "'DM Mono', monospace",
                              }}
                            >
                              #{row.serialNo}
                            </td>

                            {/* customer — name + phone combined */}
                            <td style={{ padding: "13px 14px" }}>
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: 13,
                                  color: "#0f172a",
                                  marginBottom: 2,
                                }}
                              >
                                {row.customer?.name || "—"}
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: "#94a3b8",
                                  fontFamily: "'DM Mono', monospace",
                                }}
                              >
                                {row.customer?.phone || "—"}
                              </div>
                            </td>

                            {/* section */}
                            <td style={{ padding: "13px 14px" }}>
                              <SectionPill section={row.section} />
                            </td>

                            {/* status */}
                            <td style={{ padding: "13px 14px" }}>
                              <StatusPill status={row.status} />
                            </td>

                            {/* attempts */}
                            <td
                              style={{
                                padding: "13px 14px",
                                textAlign: "center",
                              }}
                            >
                              <AttemptBar
                                attempts={row.attempts}
                                status={row.status}
                              />
                            </td>

                            {/* sent at */}
                            <td
                              style={{
                                padding: "13px 14px",
                                fontSize: 11,
                                color: "#64748b",
                              }}
                            >
                              {row.sentAt ? (
                                fmt(row.sentAt)
                              ) : (
                                <span style={{ color: "#e2e8f0" }}>—</span>
                              )}
                            </td>

                            {/* action — quick send button in row */}
                            <td
                              style={{
                                padding: "13px 14px",
                                textAlign: "center",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              {row.status !== "sent" && (
                                <button
                                  className="wa-action"
                                  onClick={(e) => handleResend(e, row)}
                                  disabled={
                                    isSending || row.status === "pending"
                                  }
                                  title="Send"
                                  style={{
                                    width: 30,
                                    height: 30,
                                    borderRadius: 8,
                                    border: "1px solid #bbf7d0",
                                    background: "#f0fdf4",
                                    color: "#15803d",
                                    fontSize: 14,
                                    cursor:
                                      isSending || row.status === "pending"
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      isSending || row.status === "pending"
                                        ? 0.5
                                        : 1,
                                    transition: "all 0.15s",
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {isSending ? (
                                    <span
                                      style={{
                                        display: "inline-block",
                                        animation: "spin 0.8s linear infinite",
                                        fontSize: 13,
                                      }}
                                    >
                                      ⟳
                                    </span>
                                  ) : (
                                    "↑"
                                  )}
                                </button>
                              )}
                            </td>
                          </tr>

                          {/* expand detail */}
                          {isOpen && (
                            <tr key={`${row._id}-exp`}>
                              <td colSpan={8} style={{ padding: 0 }}>
                                <ExpandDetail
                                  row={row}
                                  onResend={(e) => handleResend(e, row)}
                                  onView={(e) => handleView(e, row)}
                                  isSending={isSending}
                                  isViewing={isViewing}
                                />
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Pagination ── */}
          {pagination.totalPages > 1 && (
            <Pagination
              page={page}
              totalPages={pagination.totalPages}
              onChange={handlePageChange}
            />
          )}

          {/* footer count */}
          <div
            style={{
              marginTop: 10,
              fontSize: 11,
              color: "#cbd5e1",
              textAlign: "right",
            }}
          >
            Page {page} of {pagination.totalPages} · {pagination.total} total
          </div>
        </div>
      </div>
    </>
  );
}

// ─── FilterSelect ─────────────────────────────────────────────────────────────
function FilterSelect({ value, onChange, placeholder, options }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: 38,
        padding: "0 12px",
        borderRadius: 9,
        border: "1px solid #e2e8f0",
        fontSize: 13,
        color: value ? "#334155" : "#94a3b8",
        background: "#fff",
        outline: "none",
        fontFamily: "'DM Sans', sans-serif",
        cursor: "pointer",
        boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
        fontWeight: value ? 600 : 400,
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
